import React from 'react';
import { FaRegBuilding } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { strings } from '../../strings/strings';
import LoadingSpinner from './LoadingSpinner';

const ViewModal = ({ isOpen, onClose, title, icon: Icon, sections, footerActions, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700 animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-primary/10 to-primary-dark/10 dark:from-primary/20 dark:to-primary-dark/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              {Icon && (
                <div className="p-2 sm:p-3 bg-primary/20 rounded-lg sm:rounded-xl flex-shrink-0">
                  <FaRegBuilding className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                  {title}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-0.5 hidden sm:block">
                  {strings.DESCRIPTION}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
            >
              <IoMdClose className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6">
          {loading ? (
            <LoadingSpinner message="Loading details..." minHeight="h-64" />
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {sections.map((section, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/50 dark:to-gray-800/50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700"
                >
                  {/* Section Header */}
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-300 dark:border-gray-600">
                    {section.icon && (
                      <div className={`p-1.5 sm:p-2 rounded-lg ${section.iconBg || 'bg-primary/20'}`}>
                        <section.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${section.iconColor || 'text-primary'}`} />
                      </div>
                    )}
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                      {section.title}
                    </h3>
                  </div>

                  {/* Section Content */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {section.fields.map((field, fieldIndex) => (
                      <div
                        key={fieldIndex}
                        className={field.fullWidth ? 'sm:col-span-2' : ''}
                      >
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {field.label}
                        </label>
                        {field.render ? (
                          field.render(field.value)
                        ) : field.badge ? (
                          <span
                            className={`inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${field.badgeClass}`}
                          >
                            {field.value || 'N/A'}
                          </span>
                        ) : (
                          <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white break-words">
                            {field.value || 'N/A'}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-0 text-sm sm:text-base"
          >
            {strings.CLOSE}
          </button>
          {!loading && footerActions}
        </div>
      </div>
    </div>
  );
};

export default ViewModal;
